import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useAtomValue } from 'jotai/index';
import { toast } from 'sonner';
import { BaseButton, SettingsContainer } from 'wbs-components-lib';

import { Typography } from '@mui/material';
import { Box } from '@mui/system';

import { useModal } from '@shared/hooks';
import { TelegramChatModel } from '@shared/types';

import { useLazyGetOrderedAllTelegramChatsQuery } from '@services';

import {
  AppDispatch,
  getAssignedTelegramChatListSelector,
  getAssignedTelegramChatPageSelector,
  getAssignedTelegramChatTotalCountSelector,
  getInitialTelegramChatListSelector,
  getInitialTelegramChatPageSelector,
  getInitialTelegramChatTotalCountSelector,
  setAssignedTelegramChatList,
  setAssignedTelegramChatPage,
  setAssignedTelegramChatTotalCount,
  setInitialTelegramChatList,
  setInitialTelegramChatPage,
  setInitialTelegramChatTotalCount,
  updateAssignedTelegramChat,
  updateTelegramChatsChatStatus,
  updateInitialTelegramChat,
  setProcessingTelegramChat,
} from '@store';

import { TelegramChatSettingsTableTypesEnum, TelegramChatSettingsTabsEnum } from './constants';
import { TelegramChatSettingsPageTabs } from './TelegramChatSettingsPageTabs';
import { TelegramChatSettingsTable } from './TelegramChatSettingsTable';

import styles from './TelegramChatSettingsTab.module.css';

import { userAtom } from 'v2/atoms/userAtom';
import { SSEEvent } from 'v2/constants/sse';

const { settingsHeader } = styles;

export const TelegramChatSettingsTab = () => {
  const dispatch = useDispatch<AppDispatch>();

  const [currentTab, setCurrentTab] = useState<TelegramChatSettingsTabsEnum>(TelegramChatSettingsTabsEnum.ASSIGNED);

  const [isChatModelOpened, openChatModal, closeChatModal] = useModal();

  const setTab = (value: TelegramChatSettingsTabsEnum) => {
    setCurrentTab(value);
  };

  const handleChatModalOpen = (telegramChat: TelegramChatModel) => {
    dispatch(setProcessingTelegramChat(telegramChat));

    openChatModal();
  };

  const handleChatModalClose = () => {
    dispatch(setProcessingTelegramChat({} as TelegramChatModel));

    closeChatModal();
  };

  const user = useAtomValue(userAtom);

  const debouncedSendNotification = useCallback((userId: string, userNickname: string) => {
    toast.info(
      <>
        <Typography fontWeight={800} marginBottom="10px">
          You have new message!
        </Typography>
        <BaseButton onClick={() => handleChatModalOpen({ userId, userNickname } as TelegramChatModel)}>
          Open chat
        </BaseButton>
      </>
    );
  }, []);

  useEffect(() => {
    const eventSource = new EventSource(`api/v1/admin/telegram-chat/${user?.id}/status/events`);

    eventSource.addEventListener(SSEEvent.CHAT_STATUS, async (event) => {
      const { userId, userNickname, isChatMessagesRead }: TelegramChatModel = JSON.parse(event.data);

      dispatch(updateTelegramChatsChatStatus({ userId, isChatMessagesRead }));

      if (!isChatMessagesRead) {
        debouncedSendNotification(userId, userNickname);
      }
    });

    eventSource.onerror = () => {
      console.error('SSE connection error');

      eventSource.close();
    };

    return () => {
      eventSource.close();
    };
  }, [user, debouncedSendNotification]);

  const renderTelegramChatPageByCurrentTab = () => {
    switch (currentTab) {
      case TelegramChatSettingsTabsEnum.ASSIGNED: {
        return (
          <>
            <TelegramChatSettingsTable
              pageSelector={getAssignedTelegramChatPageSelector}
              setPageAction={setAssignedTelegramChatPage}
              totalCountSelector={getAssignedTelegramChatTotalCountSelector}
              setTotalCountAction={setAssignedTelegramChatTotalCount}
              listSelector={getAssignedTelegramChatListSelector}
              setListAction={setAssignedTelegramChatList}
              useLazyListQuery={useLazyGetOrderedAllTelegramChatsQuery}
              updateTelegramChat={updateAssignedTelegramChat}
              telegramChatTableType={TelegramChatSettingsTableTypesEnum.CARD_ASSIGNED}
              handleChatModalOpen={handleChatModalOpen}
              handleChatModalClose={handleChatModalClose}
              isChatModelOpened={isChatModelOpened}
            />
          </>
        );
      }
      case TelegramChatSettingsTabsEnum.ALL: {
        return (
          <TelegramChatSettingsTable
            pageSelector={getInitialTelegramChatPageSelector}
            setPageAction={setInitialTelegramChatPage}
            totalCountSelector={getInitialTelegramChatTotalCountSelector}
            setTotalCountAction={setInitialTelegramChatTotalCount}
            listSelector={getInitialTelegramChatListSelector}
            setListAction={setInitialTelegramChatList}
            useLazyListQuery={useLazyGetOrderedAllTelegramChatsQuery}
            updateTelegramChat={updateInitialTelegramChat}
            telegramChatTableType={TelegramChatSettingsTableTypesEnum.ALL}
            handleChatModalOpen={handleChatModalOpen}
            handleChatModalClose={handleChatModalClose}
            isChatModelOpened={isChatModelOpened}
          />
        );
      }
      default: {
        return null;
      }
    }
  };

  return (
    <SettingsContainer className="w-full">
      <>
        <Box className={settingsHeader}>
          <TelegramChatSettingsPageTabs setTab={setTab} currentTab={currentTab} />
        </Box>
        {renderTelegramChatPageByCurrentTab()}
      </>
    </SettingsContainer>
  );
};
